import supabase, { BUCKET_NAME, getSupabaseClient } from './supabase';

export interface UploadResult {
  success: boolean;
  url?: string;
  error?: string;
}

export const uploadImage = async (file: File): Promise<UploadResult> => {
  try {
    // Check if Supabase is properly configured
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseAnonKey) {
      return {
        success: false,
        error: 'Supabase configuration missing. Please set up environment variables.'
      };
    }

    // Ensure Supabase client is initialized
    if (!supabase) {
      return {
        success: false,
        error: 'Supabase client not initialized. Please check your environment variables.'
      };
    }

    // Development mode: Return mock URL if RLS is blocking uploads
    const isDevelopment = import.meta.env.DEV;
    if (isDevelopment && import.meta.env.VITE_MOCK_UPLOADS === 'true') {
      console.log('🔧 Development mode: Using mock image upload');
      return {
        success: true,
        url: `https://via.placeholder.com/400x300/4f46e5/white?text=${encodeURIComponent(file.name)}`
      };
    }

    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 15);
    const fileExtension = file.name.split('.').pop();
    const fileName = `setup-${timestamp}-${randomString}.${fileExtension}`;

    // Get Supabase client (will throw if not initialized)
    const supabaseClient = getSupabaseClient();

    // Upload file to Supabase Storage
    const { data, error } = await supabaseClient.storage
      .from(BUCKET_NAME)
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Supabase upload error:', error);
      console.error('Error details:', {
        message: error.message,
        statusCode: error.statusCode,
        error: error.error
      });
      
      // Provide specific error messages for common issues
      if (error.message.includes('Bucket not found')) {
        return {
          success: false,
          error: `Storage bucket '${BUCKET_NAME}' not found. Please create it in your Supabase dashboard under Storage.`
        };
      }
      
      if (error.message.includes('row-level security policy') || error.message.includes('RLS')) {
        return {
          success: false,
          error: `🚨 SUPABASE SETUP REQUIRED: Row Level Security is blocking uploads.

QUICK FIX (Development):
1. Go to https://supabase.com/dashboard → Your Project
2. Storage → Settings → Find '${BUCKET_NAME}' bucket
3. Uncheck "Enable RLS" (temporary fix)

PROPER FIX (Production):
1. Go to Storage → Policies
2. Click "New Policy" and run:

CREATE POLICY "Allow public uploads to ${BUCKET_NAME}" 
ON storage.objects 
FOR INSERT 
WITH CHECK (bucket_id = '${BUCKET_NAME}');

CREATE POLICY "Allow public reads from ${BUCKET_NAME}" 
ON storage.objects 
FOR SELECT 
USING (bucket_id = '${BUCKET_NAME}');

DEVELOPMENT WORKAROUND:
Add VITE_MOCK_UPLOADS=true to your .env file to use placeholder images.`
        };
      }
      
      return {
        success: false,
        error: error.message
      };
    }

    // Get public URL
    const { data: urlData } = supabaseClient.storage
      .from(BUCKET_NAME)
      .getPublicUrl(fileName);

    return {
      success: true,
      url: urlData.publicUrl
    };
  } catch (error) {
    console.error('Image upload error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

export const uploadMultipleImages = async (files: File[]): Promise<{ success: boolean; urls: string[]; errors: string[] }> => {
  const results = await Promise.all(files.map(file => uploadImage(file)));
  
  const urls: string[] = [];
  const errors: string[] = [];
  
  results.forEach((result, index) => {
    if (result.success && result.url) {
      urls.push(result.url);
    } else {
      errors.push(`File ${files[index].name}: ${result.error || 'Upload failed'}`);
    }
  });

  return {
    success: urls.length > 0,
    urls,
    errors
  };
};

